/**
 * Homologación de Modelos IA.
 *
 * Qué versión concreta de un modelo de IA sirve para el trabajo de
 * desarrollo (ISO/IEC 42001 A.6.2.4 y, al adoptarlo, A.6.2.5). El harness
 * ValidacionIA califica al modelo y exporta un archivo
 * (solser.homologacion-modelo v1); COS lo carga, lo muestra contra el modelo
 * vigente y una persona firma el dictamen.
 *
 * COS NO CALIFICA: la pantalla pinta lo que viene del archivo y del
 * servidor; no recalcula ningún número.
 *
 * El texto del archivo lo escribió un modelo: se pinta SOLO con
 * interpolación de Angular (que escapa). Nunca se inyecta como HTML: ni
 * binding de HTML crudo, ni rodeos del sanitizador, ni el `html:` o el
 * `title:` de Swal.
 */

/* ============================== Catálogos ============================== */

export type Veredicto =
  | "BASELINE"
  | "APROBADO"
  | "APROBADO_CON_RESERVAS"
  | "ESCALAR_A_DECISION_HUMANA"
  | "RECHAZADO"
  | "SIN_VEREDICTO";

export const VEREDICTO_TEXTO: Record<Veredicto, string> = {
  BASELINE: "Baseline",
  APROBADO: "Aprobado",
  APROBADO_CON_RESERVAS: "Aprobado con reservas",
  ESCALAR_A_DECISION_HUMANA: "Escalar a decisión humana",
  RECHAZADO: "Rechazado",
  SIN_VEREDICTO: "Sin veredicto",
};

export type SituacionModelo = "SIN_EVALUAR" | "EVALUADO" | "HOMOLOGADO" | "NO_HOMOLOGADO" | "VIGENTE" | "RETIRADO";

export const SITUACION_TEXTO: Record<SituacionModelo, string> = {
  SIN_EVALUAR: "Sin evaluar",
  EVALUADO: "Evaluado · sin dictamen",
  HOMOLOGADO: "Homologado",
  NO_HOMOLOGADO: "No homologado",
  VIGENTE: "Vigente",
  RETIRADO: "Retirado",
};

export type EstatusHomologacion = "CARGADA" | "REEMPLAZADA" | "FIRMADA";
export type DictamenHomologacion = "ADOPTAR" | "ADOPTAR_CON_RESTRICCIONES" | "NO_ADOPTAR";

export const DICTAMEN_TEXTO: Record<DictamenHomologacion, string> = {
  ADOPTAR: "Adoptar",
  ADOPTAR_CON_RESTRICCIONES: "Adoptar con restricciones",
  NO_ADOPTAR: "No adoptar",
};

export type EstadoGate = "pasa" | "falla" | "indeterminado" | "omitido";

export const ESTADO_GATE_TEXTO: Record<EstadoGate, string> = {
  pasa: "Pasa",
  falla: "Falla",
  indeterminado: "Indeterminado",
  omitido: "Omitido",
};

export const ACCION_TEXTO: Record<string, string> = {
  ALTA: "Alta",
  EDICION: "Edición",
  BAJA: "Retiro",
  CARGA: "Carga",
  REEMPLAZO: "Reemplazo",
  ELIMINACION: "Eliminación",
  FIRMA: "Firma",
  REAPERTURA: "Reapertura",
  VIGENTE: "Vigente",
  FICHA_CARGA: "Ficha cargada",
  FICHA_REEMPLAZO: "Ficha reemplazada",
  FICHA_ELIMINACION: "Ficha eliminada",
};

/* ====================== Bloques de presentación ====================== */

export type EstadoBloque = "ok" | "falla" | "aviso" | "neutro";

export interface BloqueHechos {
  tipo: "hechos";
  titulo: string;
  items: { etiqueta: string; valor: string; estado?: EstadoBloque }[];
}

export interface BloqueTabla {
  tipo: "tabla";
  titulo: string;
  columnas: string[];
  filas: string[][];
  estados?: EstadoBloque[];
}

export interface BloqueLista {
  tipo: "lista";
  titulo: string;
  items: string[];
}

export interface BloqueTexto {
  tipo: "texto";
  titulo: string;
  texto: string;
}

/** Un tipo que esta versión de COS no conoce: se avisa, no se rompe. */
export interface BloqueDesconocido {
  tipo: string;
  titulo: string;
}

export type Bloque = BloqueHechos | BloqueTabla | BloqueLista | BloqueTexto | BloqueDesconocido;

/* ===================== El documento (lo que se lee) ===================== */

/** Lo que la pantalla lee del archivo. Lo demás que traiga se ignora. */
export interface DocHomologacion {
  formato: string;
  version_formato: number;
  corrida?: { run_id?: string; repeticiones?: number };
  modelo?: { candidato_id?: string; identificador?: string; nombre_configuracion?: string; invocacion?: string };
  rubrica?: {
    rubros?: { id: string; nombre: string; peso: number; que_mide?: string }[];
    gates?: { id: string; descripcion: string; umbral?: string | null }[];
  };
  resultado?: { veredicto?: Veredicto; razones?: string[]; advertencias?: string[] };
  tareas?: DocTarea[];
}

export interface DocTarea {
  id: string;
  nombre: string;
  descripcion?: string;
  peso: number;
  umbral_resuelta?: number;
  rubros?: string[];
  esperado?: Bloque[];
  obtenido?: {
    capacidad_media: number | null;
    capacidad_desv?: number | null;
    resuelta: boolean;
    rubros?: Record<string, number | null>;
    costo_usd?: number | null;
    latencia_media_s?: number | null;
    repeticiones?: number;
    fallas_infra?: number;
  };
  ejecuciones?: DocEjecucion[];
}

export interface DocEjecucion {
  repeticion: number;
  ok: boolean;
  error_infra?: string | null;
  iniciado_en?: string | null;
  terminado_en?: string | null;
  duracion_s?: number | null;
  costo_usd?: number | null;
  tokens?: { entrada?: number; salida?: number; cache_lectura?: number; cache_escritura?: number } | null;
  turnos?: number | null;
  entrega?: { rama?: string; sha?: string } | null;
  archivos?: { modificados?: number; creados?: number; borrados?: number } | null;
  puntajes?: DocPuntaje[];
}

export interface DocPuntaje {
  rubro: string;
  nombre?: string;
  valor: number | null;
  aplica: boolean;
  metodo?: string;
  notas?: string[];
  evidencia?: Bloque[];
}

/* ============================ Lo tipado ============================ */

export interface HomologacionModelo {
  idModelo?: number;
  nombre?: string;
  identificador?: string;
  proveedor?: string | null;
  usoPrevisto?: string | null;
  idInventarioIa?: number | null;
  inventarioIa?: string | null;
  idEvalProveedor?: number | null;
  evalProveedor?: string | null;
  evalProveedorEstatus?: string | null;
  evalProveedorDictamen?: string | null;
  vigente?: boolean;
  /** La evaluación firmada «Adoptar» que lo hizo vigente: la configuración adoptada (spec 5.7). */
  idEvalVigente?: number | null;
  /** El nombre_configuracion de idEvalVigente. */
  configuracionVigente?: string | null;
  /** Cuántas configuraciones (candidato_id) tienen evaluaciones no reemplazadas. */
  configuraciones?: number;
  activo?: boolean;
  idUsuarioAlta?: number;
  alta?: string;
  fechaAlta?: string;
  situacion?: SituacionModelo;
  ultimaEvaluacion?: HomologacionEval | null;
  evaluaciones?: number;
  /** La ficha del catálogo vigente del modelo (spec 5.8); null si no tiene. */
  idFichaVigente?: number | null;
  /** El «verificado» de esa ficha, yyyy-MM-dd. */
  fichaVerificado?: string | null;
}

export interface HomologacionEval {
  idEval: number;
  idModelo: number;
  modelo?: string;
  identificador?: string;
  runId: string;
  candidatoId?: string;
  nombreConfiguracion?: string;
  esBaseline?: boolean;
  idEvalBaseline?: number | null;
  veredicto: Veredicto;
  capacidad?: number | null;
  deltaCapacidad?: number | null;
  costoRelativo?: number | null;
  p95Relativo?: number | null;
  costoTotalUsd?: number | null;
  costoPorTareaUsd?: number | null;
  tokensTotales?: number | null;
  latenciaP50S?: number | null;
  latenciaP95S?: number | null;
  consistenciaDesv?: number | null;
  consistenciaScore?: number | null;
  tareasResueltas?: number;
  tareasTotales?: number;
  ejecucionesValidas?: number;
  fallasInfra?: number;
  gatesPasan?: boolean;
  hashRubrica?: string;
  hashSuite?: string;
  hashPrompts?: string;
  perfilRubrica?: string | null;
  versionRubrica?: string | null;
  repeticiones?: number;
  aplicadaDesde?: string | null;
  aplicadaHasta?: string | null;
  calificadaHasta?: string | null;
  aplicadaEn?: string | null;
  repoRegistro?: string | null;
  ramaRegistro?: string | null;
  repoCalificacion?: string | null;
  ramaCalificacion?: string | null;
  commitCalificacion?: string | null;
  repoExportacion?: string | null;
  ramaExportacion?: string | null;
  generadoEn?: string | null;
  versionFormato?: number;
  nombreArchivo?: string;
  tamanoBytes?: number;
  sha256Archivo?: string;
  estatus: EstatusHomologacion;
  idEvalReemplazada?: number | null;
  motivoReemplazo?: string | null;
  idUsuarioCarga?: number;
  cargo?: string | null;
  fechaCarga?: string;
  dictamen?: DictamenHomologacion | null;
  dictamenRestricciones?: string | null;
  dictamenComentario?: string | null;
  firmaDigital?: string | null;
  idUsuarioFirma?: number | null;
  firmo?: string | null;
  fechaFirma?: string | null;
  /** La ficha del catálogo vigente al firmar (spec 5.8). Nula hasta que se firma. */
  idFicha?: number | null;
  /** Los precios_usd_mtok con que midió el harness esta evaluación (USD por millón de tokens); null si el archivo no los trae. */
  precioEntradaUsd?: number | null;
  precioSalidaUsd?: number | null;
  precioCacheLecturaUsd?: number | null;
  rubros?: HomologacionRubro[];
  gates?: HomologacionGate[];
  tareas?: HomologacionTarea[];
}

export interface HomologacionRubro {
  idEval?: number;
  rubro: string;
  orden: number;
  nombre: string;
  peso: number;
  queMide?: string | null;
  valor?: number | null;
  valorBaseline?: number | null;
  delta?: number | null;
}

export interface HomologacionGate {
  idEval?: number;
  gate: string;
  orden: number;
  estado: EstadoGate;
  valor?: number | null;
  umbral?: string | null;
  descripcion?: string | null;
  detalle?: string | null;
}

export interface HomologacionTarea {
  idEval?: number;
  tareaId: string;
  orden: number;
  nombre: string;
  peso: number;
  repeticiones: number;
  capacidadMedia?: number | null;
  capacidadDesv?: number | null;
  resuelta: boolean;
  costoUsd?: number | null;
  latenciaMediaS?: number | null;
}

export interface HomologacionHistorial {
  idHistorial: number;
  idModelo: number;
  idEval?: number | null;
  accion: string;
  detalle?: string | null;
  idUsuario?: number;
  usuario?: string | null;
  fecha: string;
}

export interface HomologacionPlan {
  huella?: string | null;
  nombreArchivo?: string;
  tamanoBytes?: number;
  versionFormato?: number | null;
  errores: string[];
  avisos: string[];
  puedeCargar: boolean;
  pideMotivo: boolean;
  idEvalReemplaza?: number | null;
  fechaReemplaza?: string | null;
  runId?: string | null;
  candidatoId?: string | null;
  identificador?: string | null;
  nombreConfiguracion?: string | null;
  esBaseline?: boolean | null;
  aplicadaDesde?: string | null;
  aplicadaHasta?: string | null;
  calificadaHasta?: string | null;
  repeticiones?: number | null;
  tareas?: number | null;
  ejecuciones?: number | null;
  hashRubrica?: string | null;
  hashSuite?: string | null;
  hashPrompts?: string | null;
  baselineRunId?: string | null;
  baselineNombreConfiguracion?: string | null;
  baselineCapacidad?: number | null;
  idEvalBaseline?: number | null;
  veredicto?: Veredicto | null;
  capacidad?: number | null;
  deltaCapacidad?: number | null;
}

export interface HomologacionKpis {
  vigente?: string | null;
  /** La configuración que se adoptó del vigente: «Vigente: modelo · configuración». */
  vigenteConfiguracion?: string | null;
  vigenteCapacidad?: number | null;
  modelos?: number;
  evaluaciones?: number;
  pendientes?: number;
}

/** Un activo del Inventario IA o la evaluación de un proveedor, para ligar un modelo. */
export interface HomologacionLiga {
  id: number;
  nombre: string;
  folio?: string | null;
  estatus?: string | null;
  dictamen?: string | null;
}

export interface HomologacionReq {
  idModelo?: number;
  idEval?: number;
  /** Una ficha del catálogo: ficha, fichaDescarga y fichaEliminar. */
  idFicha?: number;
  modelo?: HomologacionModelo;
  motivo?: string;
  pin?: string;
  dictamen?: DictamenHomologacion;
  restricciones?: string;
  comentario?: string;
  firma?: string;
  hashRubrica?: string;
  hashSuite?: string;
  hashPrompts?: string;
}

export interface HomologacionRes {
  modelos?: HomologacionModelo[];
  kpis?: HomologacionKpis;
  /** Solo en modelos: si mi perfil tiene la llave autorizar de Homologación. */
  autoriza?: boolean;
  modelo?: HomologacionModelo;
  inventario?: HomologacionLiga[];
  evalProveedores?: HomologacionLiga[];
  evaluaciones?: HomologacionEval[];
  evaluacion?: HomologacionEval;
  /** El archivo cargado, parseado. Solo en el detalle de gestión. */
  documento?: DocHomologacion;
  /** El archivo tal cual, para descargarlo byte por byte. Solo en descarga.do. */
  documentoTexto?: string;
  baseline?: HomologacionEval;
  historial?: HomologacionHistorial[];
  plan?: HomologacionPlan;
  /** La vista previa de una ficha del catálogo (fichaPrevia.do). */
  fichaPlan?: HomologacionFichaPlan;
  /** Una ficha completa (ficha.do); en fichaDescarga.do, solo su encabezado. null: el modelo no tiene. */
  ficha?: HomologacionFicha | null;
  /** Las fichas de un modelo, la vigente primero: encabezados, sin listas ni benchmarks (fichas.do). */
  fichas?: HomologacionFicha[];
  idGenerado?: number;
  avisos?: string[];
  codigoError?: string;
  mensajeError?: string;
}

/** Las versiones del formato que esta pantalla sabe leer a detalle. */
export const VERSIONES_QUE_LEE = [1];

/* ======================== Ficha del catálogo (spec 5.8) ======================== */
/*
 * Lo que publica el fabricante y opina el Catálogo de Modelos de IA, del
 * JSON que el catálogo exporta tal cual (Codigo_02/docs/FORMATO-FICHA-MODELO.md;
 * la v1 con `formato` se sigue aceptando). Informa;
 * no califica: no toca veredicto, gates, capacidad ni dictamen. Sale de
 * columnas y tablas hijas: el servidor nunca lee el archivo para mostrarla.
 * La escribió un tercero: se pinta SOLO con interpolación; una liga solo si
 * es https://; logoUrl no viaja.
 */

export type EstatusFicha = "VIGENTE" | "REEMPLAZADA";

export interface HomologacionFichaBenchmark {
  nombre: string;
  puntuacion?: number | null;
  unidad?: string | null;
  condicion?: string | null;
  fuenteUrl?: string | null;
}

export interface HomologacionFicha {
  idFicha?: number;
  idModelo?: number;
  /** 1 para la exportación del catálogo (sin `formato`) y para la v1 de antes. La pantalla no la muestra. */
  versionFormato?: number | null;
  catalogo?: string | null;
  /** «yyyy-MM-dd HH:mm», hora de México. */
  generado?: string | null;
  /** «yyyy-MM-dd». */
  verificado?: string | null;
  aviso?: string | null;
  /** La página del modelo en el catálogo. Se liga solo si es https://; si no, se ve como texto. */
  enlace?: string | null;
  /** Días desde «verificado», los cuenta el servidor. Más de 90: aviso, no bloquea. */
  diasVerificada?: number | null;
  modelId?: string | null;
  nombre?: string | null;
  empresa?: string | null;
  descripcion?: string | null;
  tipo?: string | null;
  acceso?: string | null;
  estadoModelo?: string | null;
  /** `modelo.currency` si la trae; si no, USD: el catálogo publica los precios de API en dólares. */
  moneda?: string | null;
  /** Precios por millón de tokens, en la moneda de la ficha. Caché, batch y Fast solo si el archivo trae `prices`. */
  precioEntrada?: number | null;
  precioSalida?: number | null;
  precioCacheEscritura5m?: number | null;
  precioCacheEscritura1h?: number | null;
  precioCacheLectura?: number | null;
  precioBatchEntrada?: number | null;
  precioBatchSalida?: number | null;
  precioFastEntrada?: number | null;
  precioFastSalida?: number | null;
  detallePrecios?: string | null;
  contextoTokens?: number | null;
  salidaMaxTokens?: number | null;
  salidaMaxTexto?: string | null;
  corteConocimiento?: string | null;
  fechaLanzamiento?: string | null;
  fechaVerificadoModelo?: string | null;
  indiceInteligencia?: number | null;
  /**
   * Con qué configuración se midió el índice, si el archivo la dice (la
   * exportación del catálogo no la trae): sin ella se ve «configuración no indicada».
   */
  indiceConfiguracion?: string | null;
  indiceFuenteUrl?: string | null;
  /** El lugar del modelo en todo el catálogo, según el catálogo. */
  topRank?: number | null;
  topRankPorQue?: string | null;
  /** El lugar del modelo dentro de su categoría, según el catálogo. */
  categoryRank?: number | null;
  categoryRankPorQue?: string | null;
  fuenteUrl?: string | null;
  fuenteEtiqueta?: string | null;
  notas?: string | null;
  accesos?: string[] | null;
  modalidades?: string[] | null;
  caracteristicas?: string[] | null;
  casosUso?: string[] | null;
  fortalezas?: string[] | null;
  limitaciones?: string[] | null;
  benchmarks?: HomologacionFichaBenchmark[] | null;
  /**
   * Los precios_usd_mtok de la última evaluación no reemplazada del modelo;
   * null si no hay. La pantalla compara contra la evaluación que está a la
   * vista (app-ficha-catalogo [preciosEval]); estos solo si no se la pasan.
   */
  preciosHarnessEntrada?: number | null;
  preciosHarnessSalida?: number | null;
  preciosHarnessCacheLectura?: number | null;
  nombreArchivo?: string | null;
  tamanoBytes?: number | null;
  sha256?: string | null;
  estatus?: EstatusFicha;
  idFichaReemplazada?: number | null;
  motivoReemplazo?: string | null;
  /** Quién la cargó. */
  cargo?: string | null;
  /** «yyyy-MM-dd HH:mm». */
  fechaCarga?: string | null;
}

/** fichaPrevia.do: el mismo contrato que HomologacionPlan (errores, avisos, huella, motivo). */
export interface HomologacionFichaPlan {
  huella?: string | null;
  nombreArchivo?: string;
  tamanoBytes?: number;
  versionFormato?: number | null;
  errores: string[];
  avisos: string[];
  puedeCargar: boolean;
  pideMotivo: boolean;
  idFichaReemplaza?: number | null;
  verificadoReemplaza?: string | null;
  /** Lo que se leyó, sin ids, para el resumen antes de confirmar. null si el archivo no se pudo leer. */
  ficha?: HomologacionFicha | null;
}

/** La franja «Según el catálogo» de la comparativa: los números de la ficha vigente de un modelo. */
export interface HomologacionFichaFranja {
  idModelo: number;
  idFicha?: number | null;
  verificado?: string | null;
  diasVerificada?: number | null;
  moneda?: string | null;
  precioEntrada?: number | null;
  precioSalida?: number | null;
  contextoTokens?: number | null;
  salidaMaxTokens?: number | null;
  /** maxOutput del catálogo, como texto: lo que se pinta si no viene el número. */
  salidaMaxTexto?: string | null;
  fechaLanzamiento?: string | null;
  indiceInteligencia?: number | null;
  indiceConfiguracion?: string | null;
  topRank?: number | null;
}

/* ============================== Consulta ============================== */
/*
 * Lo que contestan los endpoints de consulta (HomologacionConsultaService).
 * Son tipos propios, a propósito sin documento, lo esperado ni evidencia:
 * el servidor no los manda, y aquí tampoco hay dónde ponerlos.
 */

/** Una «vara»: la rúbrica, la suite y los prompts con que se midió. Solo se compara dentro de una. */
export interface HomologacionVara {
  hashRubrica: string;
  hashSuite: string;
  hashPrompts: string;
  perfilRubrica?: string | null;
  versionRubrica?: string | null;
  modelos: number;
  primeraCarga?: string | null;
  ultimaCarga?: string | null;
  /** La del modelo vigente: la que se abre por omisión. */
  delVigente?: boolean;
}

/**
 * Una configuración de un modelo con su última evaluación no reemplazada (en
 * la vara, o de cualquiera en la lista). El mismo modelo puede venir dos veces
 * con configuraciones distintas (otro effort): la llave es idEval.
 */
export interface HomologacionComparado {
  idModelo: number;
  nombre: string;
  identificador: string;
  vigente?: boolean;
  idEval: number;
  runId: string;
  /** El candidato del harness: distingue dos configuraciones del mismo modelo. */
  candidatoId?: string | null;
  /** Como la nombra el harness, con su effort: «Claude Opus 5.5 (de fábrica, effort medium)». */
  nombreConfiguracion?: string | null;
  veredicto?: Veredicto | null;
  estatus: EstatusHomologacion;
  dictamen?: DictamenHomologacion | null;
  capacidad?: number | null;
  costoPorTareaUsd?: number | null;
  costoTotalUsd?: number | null;
  latenciaP95S?: number | null;
  consistenciaDesv?: number | null;
  consistenciaScore?: number | null;
  tareasResueltas?: number | null;
  tareasTotales?: number | null;
  aplicadaDesde?: string | null;
  fechaCarga?: string | null;
  /** Se comparó contra una calificación del baseline que ya se reemplazó: hay que recalificarlo. */
  baselineReemplazado?: boolean;
}

/** Un renglón (rubro o tarea) con el valor de cada modelo y quién gana, si alguien. */
export interface HomologacionFilaComparada {
  clave: string;
  /** De una tarea viaja solo el nombre. */
  nombre: string;
  peso?: number | null;
  /** Por idEval (en JSON la llave es texto); sin entrada = no aplica. */
  valores: Record<string, number>;
  /** La evaluación (configuración) que gana. */
  idEvalLider?: number | null;
  idModeloLider?: number | null;
  margen?: number | null;
  /** true: hubo contra quién y nadie pasó el umbral. null: no hay contra quién. */
  equivalentes?: boolean | null;
}

export type TipoVentaja = "CAPACIDAD" | "COSTO" | "LATENCIA" | "RUBRO" | "TAREA";

/** «En qué es mejor cada uno»: una ventaja sobre el segundo, o que son Equivalentes. */
export interface HomologacionVentaja {
  tipo: TipoVentaja;
  clave: string;
  nombre: string;
  /** La evaluación (configuración) que gana. */
  idEvalLider?: number | null;
  idModeloLider?: number | null;
  lider?: string | null;
  /** En puntos, o como proporción si la unidad es % (0.25 = 25 %). */
  margen?: number | null;
  unidad: "pts" | "%";
  equivalentes: boolean;
}

export interface HomologacionComparativa {
  vara: HomologacionVara;
  /** Uno por modelo, en el orden en que se evaluaron. */
  modelos: HomologacionComparado[];
  rubros: HomologacionFilaComparada[];
  tareas: HomologacionFilaComparada[];
  ventajas: HomologacionVentaja[];
  /** Los de la vara que no entran: su baseline se recalificó después. Se nombran; no se comparan. */
  fuera?: HomologacionComparado[];
  /** «Según el catálogo»: una por modelo con ficha vigente. No ordena ni califica (spec 5.8). */
  franja?: HomologacionFichaFranja[];
}

/** Un gate en el Resultado resumido: sin descripción ni detalle. */
export interface HomologacionGateResumen {
  gate: string;
  orden: number;
  estado: EstadoGate;
  valor?: number | null;
  umbral?: string | null;
}

/** El Resultado resumido de la Consulta: sin lo esperado, repeticiones, evidencia, procedencia ni descarga. */
export interface HomologacionResumen {
  idEval: number;
  idModelo: number;
  modelo: string;
  identificador: string;
  vigente?: boolean;
  runId: string;
  nombreConfiguracion?: string | null;
  esBaseline?: boolean;
  baselineModelo?: string | null;
  baselineRunId?: string | null;
  /** Su baseline se recalificó después: los deltas son contra una calificación que ya no vale. */
  baselineReemplazado?: boolean;
  veredicto?: Veredicto | null;
  estatus: EstatusHomologacion;
  dictamen?: DictamenHomologacion | null;
  fechaFirma?: string | null;
  capacidad?: number | null;
  deltaCapacidad?: number | null;
  costoRelativo?: number | null;
  p95Relativo?: number | null;
  costoTotalUsd?: number | null;
  costoPorTareaUsd?: number | null;
  tokensTotales?: number | null;
  latenciaP95S?: number | null;
  consistenciaDesv?: number | null;
  consistenciaScore?: number | null;
  tareasResueltas?: number | null;
  tareasTotales?: number | null;
  repeticiones?: number | null;
  aplicadaDesde?: string | null;
  /** Los precios_usd_mtok con que midió el harness esta evaluación (de columnas); null si no los trae. */
  precioEntradaUsd?: number | null;
  precioSalidaUsd?: number | null;
  precioCacheLecturaUsd?: number | null;
  gates?: HomologacionGateResumen[];
  rubros?: HomologacionRubro[];
  tareas?: HomologacionTarea[];
}

export interface HomologacionConsultaRes {
  varas?: HomologacionVara[];
  modelos?: HomologacionComparado[];
  comparativa?: HomologacionComparativa;
  resumen?: HomologacionResumen;
  /** La ficha del catálogo vigente de un modelo (consulta/ficha.do); null si no tiene. Sin descarga. */
  ficha?: HomologacionFicha | null;
  codigoError?: string;
  mensajeError?: string;
}
